import _slicedToArray from "@babel/runtime/helpers/esm/slicedToArray";
import React, { useMemo } from "react";
import classNames from 'classnames';
import invariant from 'invariant';
import { Loading } from '@alifd/next';
import { useMcms } from "../../hooks/medusa";
import { SkeletonList } from "../skeleton";
import { LayoutContext, LocaleContext, PaginationContext, SelectionContext, StaticContext } from "./contexts";
import Toolbar from "./toolbar";
import Header from "./header";
import Item from "./item";
import Paginator from "./paginator";
import CheckCell from "./check-cell";
import useLayout from "./hooks/use-layout";
import useSelection from "./hooks/use-selection";
import { isPlainObject, getElementProps } from "./utils";
import { defaultEmptyContent, defaultRenderItem } from "./default-props";
import defaultLocale from "./default-locale";
import { Description } from "./description";
import "./index.scss";
export * from "./contexts";
export * from "./interfaces";
/**
 * List框架组件
 * @author 尼諾
 * @workNo 095691
 * @category Basic
 */
function List(props) {
  var className = props.className,
    style = props.style,
    _props$sticky = props.sticky,
    sticky = _props$sticky === void 0 ? true : _props$sticky,
    toolbar = props.toolbar,
    header = props.header,
    prefix = props.prefix,
    suffix = props.suffix,
    _props$columns = props.columns,
    columns = _props$columns === void 0 ? [] : _props$columns,
    _props$dataSource = props.dataSource,
    dataSource = _props$dataSource === void 0 ? [] : _props$dataSource,
    _props$loading = props.loading,
    loading = _props$loading === void 0 ? false : _props$loading,
    _props$loadingCompone = props.loadingComponent,
    LoadingComponent = _props$loadingCompone === void 0 ? Loading : _props$loadingCompone,
    _props$primaryKey = props.primaryKey,
    primaryKey = _props$primaryKey === void 0 ? 'id' : _props$primaryKey,
    rowSelection = props.rowSelection,
    _props$pagination = props.pagination,
    pagination = _props$pagination === void 0 ? {} : _props$pagination,
    _props$renderRow = props.renderRow,
    renderRow = _props$renderRow === void 0 ? defaultRenderItem : _props$renderRow,
    renderHeader = props.renderHeader,
    renderFooter = props.renderFooter,
    emptyContent = props.emptyContent,
    children = props.children,
    propsLocale = props.locale,
    _props$rowSpmCPrefix = props.rowSpmCPrefix,
    rowSpmCPrefix = _props$rowSpmCPrefix === void 0 ? 'table_row_' : _props$rowSpmCPrefix;
  var hasColumns = columns.length > 0;
  var pageInfo = isPlainObject(pagination) ? pagination : getElementProps(pagination);

  // Build and memorize static variables
  var staticVars = useMemo(function () {
    return {
      sticky: sticky,
      columns: columns
    };
  }, [sticky, columns]);
  var _useMcms = useMcms({
      appName: 'intl-components',
      tag: 'list',
      version: '0.0.15'
    }, defaultLocale, propsLocale),
    _useMcms2 = _slicedToArray(_useMcms, 1),
    locale = _useMcms2[0];
  var layout = useLayout();
  var selection = useSelection(dataSource, primaryKey, rowSelection);
  var bodyContent;
  if (children === undefined) {
    if (dataSource.length > 0) {
      // Render dataSource
      bodyContent = dataSource.map(function (item, index) {
        return /*#__PURE__*/React.createElement(Item, {
          header: renderHeader && renderHeader(item, index, columns),
          footer: renderFooter && renderFooter(item, index, columns),
          key: index,
          index: index,
          record: item,
          "data-spm": "".concat(rowSpmCPrefix).concat(index)
        }, renderRow(item, index, columns));
      });
    } else {
      // Display empty tips
      bodyContent = emptyContent || defaultEmptyContent(locale);
    }
  } else {
    var length = React.Children.count(children);
    if (length > 0) {
      // Process children
      // @ts-ignore
      bodyContent = React.Children.map(children, function (element, index) {
        invariant(element.type === Item, '[List]: children must be <List.Item>');
        if (element.type === Item) {
          var hasRecord = element.props.record !== undefined;
          if (hasRecord) {
            // Already has record props
            return /*#__PURE__*/React.cloneElement(element, {
              'data-spm': "".concat(rowSpmCPrefix).concat(index)
            });
          } else {
            // Inject record props
            var _record = dataSource[index];
            return /*#__PURE__*/React.cloneElement(element, {
              record: _record,
              'data-spm': "".concat(rowSpmCPrefix).concat(index)
            });
          }
        }
        return null;
      });
    } else {
      // Display empty tips
      bodyContent = emptyContent || defaultEmptyContent(locale);
    }
  }
  var result = /*#__PURE__*/React.createElement("div", {
    className: classNames('list-frame', className),
    style: style
  }, /*#__PURE__*/React.createElement(StaticContext.Provider, {
    value: staticVars
  }, /*#__PURE__*/React.createElement(LocaleContext.Provider, {
    value: locale
  }, /*#__PURE__*/React.createElement(LayoutContext.Provider, {
    value: layout
  }, /*#__PURE__*/React.createElement(SelectionContext.Provider, {
    value: selection
  }, /*#__PURE__*/React.createElement(PaginationContext.Provider, {
    value: pageInfo
  }, toolbar === undefined ? /*#__PURE__*/React.createElement(Toolbar, null) : toolbar, header === undefined && hasColumns ? /*#__PURE__*/React.createElement(Header, null) : header, prefix, /*#__PURE__*/React.createElement("div", {
    className: "list-body"
  }, bodyContent), suffix, isPlainObject(pagination) ? /*#__PURE__*/React.createElement(Paginator, null) : pagination))))));
  if (loading) {
    result = dataSource && dataSource.length > 1 ? /*#__PURE__*/React.createElement(LoadingComponent, {
      className: classNames('list-frame-loading', className),
      style: style
    }, result) : /*#__PURE__*/React.createElement(SkeletonList, {
      style: style
    });
  }
  return result;
}
List.Toolbar = Toolbar;
List.Header = Header;
List.Item = Item;
List.Description = Description;
List.CheckCell = CheckCell;
List.SelectionContext = SelectionContext;
export default List;